import React, { useCallback, useMemo, useState } from 'react';
import { View, Text, Modal, ScrollView, TouchableOpacity } from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import { v4 as uuidv4 } from 'uuid';
import {
  KdsModifierStyle,
  ModifierKeywordMatchMode,
} from '@oolio-group/domain';
import SetModifierStyleModalStyles from './SetModifierStyleModal.styles';
import DocketItemStyles from '../../DocketItem/DocketItem.styles';
import {
  validateModifierStyle,
  MAX_NAME_LENGTH,
  MAX_CONDITIONS_PER_STYLE,
} from '../../../utils/validateModifierStyle';
import { matchModifierStyle } from '../../../utils/matchModifierStyle';
import InputText from '../../Input/InputText';
import Button from '../../Buttons/ButtonText';
import Icon from '../../Icon/Icon';
import theme from '../../../common/theme';
import { useColors } from '../../../hooks/useColors';

export enum ModifierStyleAction {
  CREATE = 'create',
  EDIT = 'edit',
}

export interface SetModifierStyleModalProps {
  visible: boolean;
  action: ModifierStyleAction;
  existingStyles: KdsModifierStyle[];
  style?: KdsModifierStyle;
  onClose?: () => void;
  onDelete?: () => void;
  onSave: (style: KdsModifierStyle) => void;
}

const MAX_CONDITIONS = MAX_CONDITIONS_PER_STYLE;

// Sample dockets used for the live preview. Switching between them lets
// merchants see the same modifier style against the different order-type
// header colours a real KDS renders (dine-in blue, takeaway orange, etc.).
const PREVIEW_TIME = '2m';
const PREVIEW_ITEM_NAME = 'Flat White';
// Contrast modifiers shown alongside the keyword-derived one so merchants can
// see that options which don't match any keyword keep the default appearance.
const PREVIEW_CONTRAST_MODIFIERS = ['Extra shot', 'No sugar'];

function titleCase(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

const PREVIEW_ORDER_TYPES: {
  orderType: string;
  orderNumber: string;
  reference: string;
  headerColor: string;
}[] = [
  {
    orderType: 'Dine In',
    orderNumber: '#1042',
    reference: 'Table 6',
    headerColor: theme.colors.primary,
  },
  {
    orderType: 'Takeaway',
    orderNumber: '#1043',
    reference: 'Pickup',
    headerColor: theme.colors.orange,
  },
  {
    orderType: 'Delivery',
    orderNumber: '#1044',
    reference: 'Uber Eats',
    headerColor: theme.colors.green,
  },
];

const emptyDraft = (nextPriority: number): KdsModifierStyle => ({
  id: uuidv4(),
  name: '',
  styles: {},
  isActive: true,
  priority: nextPriority,
  conditions: [],
});

const SetModifierStyleModal: React.FC<SetModifierStyleModalProps> = ({
  visible,
  action,
  existingStyles,
  style: existingStyle,
  onClose = () => undefined,
  onDelete = () => undefined,
  onSave,
}) => {
  const { translate } = useTranslation();
  const { colors } = useColors();
  const styles = SetModifierStyleModalStyles();
  const docketStyles = DocketItemStyles();

  const [draft, setDraft] = useState<KdsModifierStyle>(
    () =>
      existingStyle ??
      emptyDraft(Math.max(0, ...existingStyles.map(s => s.priority)) + 1),
  );
  const [keywordText, setKeywordText] = useState('');
  const [previewTypeIndex, setPreviewTypeIndex] = useState(0);
  const previewOrderType = PREVIEW_ORDER_TYPES[previewTypeIndex];

  const validationError = validateModifierStyle(
    draft,
    existingStyles,
    action === ModifierStyleAction.CREATE,
  );

  // Resolve the structured validation error into a localized message. The
  // match-type (when present) is translated into its own label first.
  const validationMessage = validationError
    ? translate(`kitchenDisplay.${validationError.key}`, {
        ...validationError.params,
        ...(validationError.matchType
          ? {
              matchType: translate(
                validationError.matchType === ModifierKeywordMatchMode.EXACT
                  ? 'kitchenDisplay.modifierStyleMatchExact'
                  : 'kitchenDisplay.modifierStyleMatchContains',
              ),
            }
          : {}),
      })
    : '';

  const onChangeName = (name: string) => {
    setDraft(prev => ({ ...prev, name: name.slice(0, MAX_NAME_LENGTH) }));
  };

  const onSelectColour = (color: string) => {
    setDraft(prev => ({ ...prev, styles: { ...prev.styles, color } }));
  };

  const isNoColourSelected = !draft.styles?.color && !!draft.styles?.italic;

  const onSelectNoColour = () => {
    if (!draft.styles?.italic) return;
    setDraft(prev => ({
      ...prev,
      styles: { ...prev.styles, color: undefined },
    }));
  };

  const onToggleItalic = () => {
    setDraft(prev => ({
      ...prev,
      styles: { ...prev.styles, italic: !prev.styles?.italic },
    }));
  };

  const onAddKeyword = () => {
    const keyword = keywordText.trim();
    if (!keyword || draft.conditions.length >= MAX_CONDITIONS) return;
    setDraft(prev => ({
      ...prev,
      conditions: [
        ...prev.conditions,
        { keyword, matchType: ModifierKeywordMatchMode.CONTAINS },
      ],
    }));
    setKeywordText('');
  };

  const onChangeKeywordMatchType = (
    index: number,
    matchType: ModifierKeywordMatchMode,
  ) => {
    setDraft(prev => ({
      ...prev,
      conditions: prev.conditions.map((condition, i) =>
        i === index ? { ...condition, matchType } : condition,
      ),
    }));
  };

  const onRemoveKeyword = (index: number) => {
    setDraft(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
    }));
  };

  const onPressSave = useCallback(() => {
    if (validationError) return;
    onSave(draft);
  }, [draft, validationError, onSave]);

  const onPressDelete = useCallback(() => {
    onDelete();
  }, [onDelete]);

  // Lead the preview with a modifier built from the first keyword so the style
  // is always visible — even when the keyword wouldn't match any of the fixed
  // contrast samples — then append the contrast samples themselves.
  const previewModifiers = useMemo(() => {
    const firstKeyword = draft.conditions
      .map(condition => condition.keyword?.trim())
      .find(Boolean);
    const lead = firstKeyword ? titleCase(firstKeyword) : 'Oat milk';
    const contrast = PREVIEW_CONTRAST_MODIFIERS.filter(
      modifier => modifier.toLowerCase() !== lead.toLowerCase(),
    );
    return [lead, ...contrast];
  }, [draft.conditions]);

  // Resolve each preview modifier through the shared matcher (against just the
  // draft being edited) so the preview uses the exact same rules as the live
  // docket rendering.
  const previewMatches = useMemo(
    () =>
      previewModifiers.map(modifier => matchModifierStyle(modifier, [draft])),
    [previewModifiers, draft],
  );

  return (
    <Modal
      animationType="fade"
      onDismiss={onClose}
      onRequestClose={onClose}
      presentationStyle="overFullScreen"
      transparent={true}
      visible={visible}
      supportedOrientations={['landscape', 'landscape-left', 'landscape-right']}
    >
      <View style={styles.background}>
        <View style={styles.panel} testID="modifier-style-modal">
          <View style={styles.header}>
            <Text style={styles.title}>
              {translate(
                action === ModifierStyleAction.CREATE
                  ? 'kitchenDisplay.createModifierStyle'
                  : 'kitchenDisplay.editModifierStyle',
              )}
            </Text>
            <View style={styles.headerActions}>
              {action === ModifierStyleAction.EDIT && (
                <TouchableOpacity
                  style={styles.headerIconButton}
                  testID="btn-delete"
                  onPress={onPressDelete}
                >
                  <Icon name="trash" size={18} color={colors.text} />
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={styles.headerIconButton}
                testID="btn-dismiss"
                onPress={onClose}
              >
                <Icon name="times" size={20} color={colors.text} />
              </TouchableOpacity>
            </View>
          </View>

          <ScrollView
            style={styles.scrollArea}
            contentContainerStyle={styles.scrollContent}
          >
            <View>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>
                  {translate('kitchenDisplay.modifierStyleName')}
                </Text>
                <Text style={styles.counterText}>
                  {draft.name.length}/{MAX_NAME_LENGTH}
                </Text>
              </View>
              <InputText
                onChangeText={onChangeName}
                value={draft.name}
                containerStyle={styles.inputContainer}
                testID="input-modifier-style-name"
                placeholder={translate(
                  'kitchenDisplay.modifierStyleNamePlaceholder',
                )}
              />
            </View>

            <View>
              <Text style={[styles.fieldLabel, styles.sectionLabelSpacing]}>
                {translate('kitchenDisplay.modifierStyleColor')}
              </Text>
              <View style={styles.swatchGrid}>
                {theme.colors.pools.modifiers.map(swatch => {
                  const selected = draft.styles?.color === swatch.color;
                  return (
                    <TouchableOpacity
                      key={swatch.color}
                      testID={`swatch-${swatch.color}`}
                      style={[styles.swatch, selected && styles.swatchSelected]}
                      onPress={() => onSelectColour(swatch.color)}
                    >
                      <View
                        style={[
                          styles.swatchDot,
                          { backgroundColor: swatch.color },
                        ]}
                      />
                      <Text style={styles.swatchLabel} numberOfLines={2}>
                        {swatch.name}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <TouchableOpacity
                testID="swatch-no-colour"
                disabled={!draft.styles?.italic}
                style={[
                  styles.noColourRow,
                  isNoColourSelected && styles.swatchSelected,
                  !draft.styles?.italic && styles.noColourRowDisabled,
                ]}
                onPress={onSelectNoColour}
              >
                <View style={styles.noColourDot}>
                  <View style={styles.noColourSlash} />
                </View>
                <View style={styles.noColourTextGroup}>
                  <Text style={styles.noColourLabel}>
                    {translate('kitchenDisplay.modifierStyleNoColor')}
                  </Text>
                  <Text style={styles.noColourHelper}>
                    {translate('kitchenDisplay.modifierStyleNoColorHelper')}
                  </Text>
                </View>
              </TouchableOpacity>
            </View>

            <View>
              <Text style={[styles.fieldLabel, styles.sectionLabelSpacing]}>
                {translate('kitchenDisplay.modifierStyleTextStyle')}
              </Text>
              <View style={styles.italicToggleRow}>
                <TouchableOpacity
                  testID="toggle-modifier-style-italic"
                  style={[
                    styles.italicToggle,
                    draft.styles?.italic && styles.italicToggleSelected,
                  ]}
                  onPress={onToggleItalic}
                >
                  <Icon name="italic" size={14} color={colors.text} />
                  <Text style={styles.italicToggleLabel}>
                    {translate('kitchenDisplay.modifierStyleItalic')}
                  </Text>
                </TouchableOpacity>
                <Text style={styles.helperText}>
                  {translate('kitchenDisplay.modifierStyleBoldHelper')}
                </Text>
              </View>
            </View>

            <View>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>
                  {translate('kitchenDisplay.modifierStyleKeywords')}
                </Text>
                <Text style={styles.counterText}>
                  {draft.conditions.length}/{MAX_CONDITIONS}
                </Text>
              </View>
              <View style={styles.row}>
                <InputText
                  onChangeText={setKeywordText}
                  value={keywordText}
                  onSubmitEditing={onAddKeyword}
                  containerStyle={[styles.inputContainer, styles.keywordInput]}
                  testID="input-modifier-style-keyword"
                  placeholder={translate(
                    'kitchenDisplay.modifierStyleKeywordPlaceholder',
                  )}
                />
                <Button
                  testID="btn-add-keyword"
                  label={translate('kitchenDisplay.modifierStyleAddKeyword')}
                  type="active"
                  onPress={onAddKeyword}
                  containerStyle={styles.keywordAddButton}
                />
              </View>
              <Text style={styles.helperText}>
                {translate('kitchenDisplay.modifierStyleKeywordsHelper')}
              </Text>
              {draft.conditions.length > 0 && (
                <View style={styles.keywordList}>
                  {draft.conditions.map((condition, index) => {
                    const isExact =
                      condition.matchType === ModifierKeywordMatchMode.EXACT;
                    return (
                      <View key={index} style={styles.keywordChip}>
                        <Text numberOfLines={1} style={styles.keywordChipText}>
                          {condition.keyword}
                        </Text>
                        <View style={styles.keywordChipRight}>
                          <View style={styles.matchTypeGroup}>
                            <TouchableOpacity
                              testID={`toggle-match-exact-${index}`}
                              style={[
                                styles.matchTypeOption,
                                isExact && styles.matchTypeOptionSelected,
                              ]}
                              onPress={() =>
                                onChangeKeywordMatchType(
                                  index,
                                  ModifierKeywordMatchMode.EXACT,
                                )
                              }
                            >
                              <Text
                                style={[
                                  styles.matchTypeLabel,
                                  isExact && styles.matchTypeLabelSelected,
                                ]}
                              >
                                {translate(
                                  'kitchenDisplay.modifierStyleMatchExact',
                                )}
                              </Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                              testID={`toggle-match-contains-${index}`}
                              style={[
                                styles.matchTypeOption,
                                !isExact && styles.matchTypeOptionSelected,
                              ]}
                              onPress={() =>
                                onChangeKeywordMatchType(
                                  index,
                                  ModifierKeywordMatchMode.CONTAINS,
                                )
                              }
                            >
                              <Text
                                style={[
                                  styles.matchTypeLabel,
                                  !isExact && styles.matchTypeLabelSelected,
                                ]}
                              >
                                {translate(
                                  'kitchenDisplay.modifierStyleMatchContains',
                                )}
                              </Text>
                            </TouchableOpacity>
                          </View>
                          <TouchableOpacity
                            style={styles.keywordRemove}
                            testID={`btn-remove-keyword-${index}`}
                            onPress={() => onRemoveKeyword(index)}
                          >
                            <Icon
                              name="times"
                              size={16}
                              color={colors.textLight}
                            />
                          </TouchableOpacity>
                        </View>
                      </View>
                    );
                  })}
                </View>
              )}
              {!!validationError && (
                <Text style={styles.errorText} testID="modifier-style-error">
                  {validationMessage}
                </Text>
              )}
            </View>

            <View>
              <Text style={[styles.fieldLabel, styles.sectionLabelSpacing]}>
                {translate('kitchenDisplay.modifierStyleLivePreview')}
              </Text>
              <View style={styles.previewTypeRow}>
                {PREVIEW_ORDER_TYPES.map((preset, index) => {
                  const selected = index === previewTypeIndex;
                  return (
                    <TouchableOpacity
                      key={preset.orderType}
                      testID={`preview-order-type-${index}`}
                      style={[
                        styles.previewTypeChip,
                        selected && styles.previewTypeChipSelected,
                      ]}
                      onPress={() => setPreviewTypeIndex(index)}
                    >
                      <Text
                        style={[
                          styles.previewTypeChipText,
                          selected && styles.previewTypeChipTextSelected,
                        ]}
                      >
                        {preset.orderType}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <View style={styles.previewFrame} testID="modifier-style-preview">
                <View style={styles.previewDocket}>
                  <View
                    style={[
                      docketStyles.header,
                      { backgroundColor: previewOrderType.headerColor },
                    ]}
                  >
                    <View style={docketStyles.headerContent}>
                      <View style={docketStyles.rowGroup}>
                        <View style={docketStyles.row}>
                          <Text style={docketStyles.text}>
                            {previewOrderType.orderType}
                          </Text>
                          <Text style={docketStyles.text}>
                            {previewOrderType.orderNumber}
                          </Text>
                        </View>
                        <View style={docketStyles.row}>
                          <Text style={docketStyles.text}>
                            {previewOrderType.reference}
                          </Text>
                          <Text style={docketStyles.text}>{PREVIEW_TIME}</Text>
                        </View>
                      </View>
                    </View>
                  </View>
                  <View style={docketStyles.product}>
                    <View style={[docketStyles.line, styles.previewItemRow]}>
                      <View style={[docketStyles.qty, styles.previewQty]}>
                        <Text style={docketStyles.qtyText}>1</Text>
                      </View>
                      <Text style={docketStyles.name}>{PREVIEW_ITEM_NAME}</Text>
                    </View>
                    {previewModifiers.map((modifier, index) => {
                      const matched = previewMatches[index];
                      const color = matched?.color;
                      const italic = !!matched?.italic;
                      return (
                        <View key={modifier} style={docketStyles.line}>
                          <Text
                            style={[
                              docketStyles.modifierBase,
                              docketStyles.modifierSpacing,
                            ]}
                          >
                            {'↳ '}
                          </Text>
                          <Text
                            testID={`preview-modifier-${index}`}
                            style={[
                              docketStyles.modifierBase,
                              color ? styles.previewModifierMatched : null,
                              color ? { color } : null,
                              italic ? styles.previewModifierItalic : null,
                            ]}
                          >
                            {modifier}
                          </Text>
                        </View>
                      );
                    })}
                  </View>
                </View>
              </View>
            </View>
          </ScrollView>

          <View style={styles.actions}>
            <Button
              testID="btn-save"
              label={translate('kitchenDisplay.modifierStyleSave')}
              type={validationError ? 'interface' : 'positive'}
              onPress={onPressSave}
              disabled={!!validationError}
              containerStyle={styles.saveButton}
            />
            <Button
              testID="btn-cancel"
              label={translate('button.cancel')}
              type="active"
              onPress={onClose}
              containerStyle={styles.cancelButton}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default SetModifierStyleModal;
